import { PhoneForwarded, Plus, X } from "lucide-react"

import { CapabilityCard } from "@/components/agents/setup/capability-card"
import type { TransferPreset } from "@/components/agents/setup/types"
import { WeeklyHoursGrid } from "@/components/business-profile/hours-editor"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import type { EditableFields } from "../edit-sheet"
import { formatPhone } from "../form-specs"

type Update = <K extends keyof EditableFields>(
  key: K,
  value: EditableFields[K],
) => void

export function TransfersPanel({
  fields,
  onChange,
  transferPresets = [],
}: {
  fields: EditableFields
  onChange: Update
  transferPresets?: TransferPreset[]
}) {
  function togglePreset(id: string) {
    const next = fields.handoff_presets.includes(id)
      ? fields.handoff_presets.filter((p) => p !== id)
      : [...fields.handoff_presets, id]
    onChange("handoff_presets", next)
  }

  return (
    <CapabilityCard
      icon={PhoneForwarded}
      on={fields.transfers_enabled}
      title="Transfer calls"
      description="Puts the caller through to a person while they're still on the line."
      onToggle={(v) => onChange("transfers_enabled", v)}
    >
      <section className="space-y-2">
        <Label className="mb-4">Transfer to a human when...</Label>
        <div className="space-y-4">
          {transferPresets.map(({ id, label }) => (
            <div key={id} className="flex items-center gap-3">
              <Checkbox
                id={`edit-transfer-${id}`}
                checked={fields.handoff_presets.includes(id)}
                onCheckedChange={() => togglePreset(id)}
              />
              <Label
                htmlFor={`edit-transfer-${id}`}
                className="cursor-pointer font-normal"
              >
                {label}
              </Label>
            </div>
          ))}
          {fields.handoff_custom_reasons.map((reason, i) => (
            <div key={i} className="flex items-center gap-3">
              <Checkbox checked />
              <input
                className="h-4 flex-1 cursor-text border-none bg-transparent p-0 text-sm leading-none ring-0 outline-none"
                value={reason}
                onChange={(e) =>
                  onChange(
                    "handoff_custom_reasons",
                    fields.handoff_custom_reasons.map((r, idx) =>
                      idx === i ? e.target.value : r,
                    ),
                  )
                }
                placeholder="Describe the reason…"
              />
              <button
                type="button"
                onClick={() =>
                  onChange(
                    "handoff_custom_reasons",
                    fields.handoff_custom_reasons.filter((_, idx) => idx !== i),
                  )
                }
                className="text-muted-foreground hover:text-foreground shrink-0"
                aria-label="Remove"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              onChange("handoff_custom_reasons", [
                ...fields.handoff_custom_reasons,
                "",
              ])
            }
            className="text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm transition-colors"
          >
            <Plus className="size-4" /> Add custom reason
          </button>
        </div>
      </section>

      <section className="space-y-2">
        <Label htmlFor="handoff_phone">Transfer to</Label>
        <Input
          id="handoff_phone"
          type="tel"
          placeholder="(415) 555-0100"
          value={fields.handoff_phone}
          onChange={(e) =>
            onChange("handoff_phone", formatPhone(e.target.value))
          }
        />
      </section>

      <section className="space-y-2">
        <Label>Available hours</Label>
        {/* IMP-491: no 24h option — the transfer window has no all-day
                concept, so a day is either unavailable or has a start and end. */}
        <WeeklyHoursGrid
          hours={fields.handoff_hours}
          allowAllDay={false}
          closedLabel="Unavailable"
          onHourChange={(idx, patch) =>
            onChange(
              "handoff_hours",
              fields.handoff_hours.map((row, i) =>
                i === idx ? { ...row, ...patch } : row,
              ),
            )
          }
        />
      </section>
    </CapabilityCard>
  )
}
